import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { getCatalogId } from '../../../utils/tree/ids';
import {
  createReferenceEqualSelector,
  getCatalogIdFromTreeId,
} from '../../selectors/utils';
import { setMarket } from '../intl';
import { ReadOnlyArray } from '../../../utils/array/ReadOnlyArray';
import { SEARCH_BY, toSearchQuery } from '../../../utils/tree/search';
import type { RootState } from '../..';
import type { Item } from '../../../entities/Item';
import type { NodeBaseItem } from '../../../entities/Node';

interface SearchCondition {
  by: string;
  condition: string;
}

interface SearchQuery {
  updated?: number;
  catalogIds?: string[];
  by: string;
  condition: string;
  regexp: string;
  structureReasonText?: string | null;
  item?: Item;
}

interface SearchSuggestion {
  id: string;
  title: string;
  type: string;
  data: unknown;
}

const dedupeSearchSuggestions = (suggestions: SearchSuggestion[]) => {
  const seen = new Set<string>();
  const result: SearchSuggestion[] = [];

  for (const suggestion of suggestions) {
    const key = `${suggestion.type}:${suggestion.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(suggestion);
  }

  return result;
};

interface CatalogSearchState {
  searchCondition: SearchCondition | null;
  searchQuery: SearchQuery | null;
  searchPanel: boolean;
  searchMatches: NodeBaseItem[];
  searchSuggestions: SearchSuggestion[];
  highlightFocusId: string;
  highlightIndex: string;
  searchFocusIndex: number | null;
  searchFocusId: string | null;
}

interface TreeSearchState {
  catalogs: Record<string, CatalogSearchState>;
}

const initialCatalogSearchState: CatalogSearchState = {
  searchCondition: null,
  searchQuery: null,
  searchPanel: false,
  searchMatches: [],
  searchSuggestions: [],
  highlightFocusId: '',
  highlightIndex: '',
  searchFocusIndex: null,
  searchFocusId: null,
};

const initialState: TreeSearchState = {
  catalogs: {},
};

const treeSearchSlice = createSlice({
  name: 'treeSearch',
  initialState,
  reducers: {
    setSearchCondition: {
      prepare(tree: string, by: string, condition?: string) {
        return {
          meta: { tree },
          error: null,
          payload: { by, condition: condition?.trim() },
        };
      },
      reducer(
        state,
        action: PayloadAction<
          { by: string; condition?: string },
          string,
          { tree: string }
        >,
      ) {
        const {
          meta: { tree },
          payload: { by, condition },
        } = action;
        const catalogId = getCatalogId(tree);

        if (!state.catalogs[catalogId]) {
          state.catalogs[catalogId] = { ...initialCatalogSearchState };
        }

        if (!condition) {
          state.catalogs[catalogId].searchCondition = null;
          state.catalogs[catalogId].searchQuery = null;
          state.catalogs[catalogId].searchSuggestions = [];
          return;
        }

        state.catalogs[catalogId].searchCondition = { by, condition };
        state.catalogs[catalogId].searchQuery = null;
      },
    },
    setSearchSuggestions(
      state,
      action: PayloadAction<{ catalogId: string; suggestions: SearchSuggestion[] }>,
    ) {
      const { catalogId, suggestions } = action.payload;
      if (!state.catalogs[catalogId]) {
        state.catalogs[catalogId] = { ...initialCatalogSearchState };
      }
      state.catalogs[catalogId].searchSuggestions =
        dedupeSearchSuggestions(suggestions);
    },
    setSearchResults(
      state,
      action: PayloadAction<{
        catalogId: string;
        searchQuery: SearchQuery;
        searchMatches: NodeBaseItem[];
        searchFocusIndex: number | null;
        searchFocusId: string | null;
      }>,
    ) {
      const {
        catalogId,
        searchQuery,
        searchMatches,
        searchFocusIndex,
        searchFocusId,
      } = action.payload;
      if (!state.catalogs[catalogId]) {
        state.catalogs[catalogId] = { ...initialCatalogSearchState };
      }
      state.catalogs[catalogId].searchQuery = searchQuery;
      state.catalogs[catalogId].searchMatches = searchMatches;
      state.catalogs[catalogId].searchFocusIndex = searchFocusIndex;
      state.catalogs[catalogId].searchFocusId = searchFocusId;
    },
    setSearchFocus(
      state,
      action: PayloadAction<{
        catalogId: string;
        searchFocusIndex: number | null;
        searchFocusId: string | null;
      }>,
    ) {
      const { catalogId, searchFocusIndex, searchFocusId } = action.payload;
      if (state.catalogs[catalogId]) {
        state.catalogs[catalogId].searchFocusIndex = searchFocusIndex;
        state.catalogs[catalogId].searchFocusId = searchFocusId;
      }
    },
    moveSearchNext: {
      prepare(tree: string) {
        return { meta: { tree }, payload: null, error: null };
      },
      reducer(state, action: PayloadAction<null, string, { tree: string }>) {
        const catalogId = getCatalogId(action.meta.tree);
        const catalog = state.catalogs[catalogId];
        if (!catalog) return;

        const currentIndex = catalog.searchFocusIndex ?? 0;
        const matchCount = catalog.searchMatches.length;
        if (matchCount === 0) return;

        const newIndex = currentIndex >= matchCount - 1 ? 0 : currentIndex + 1;
        catalog.searchFocusIndex = newIndex;
        catalog.searchFocusId = catalog.searchMatches[newIndex]?.node.id ?? null;
      },
    },
    moveSearchPrevious: {
      prepare(tree: string) {
        return { meta: { tree }, payload: null, error: null };
      },
      reducer(state, action: PayloadAction<null, string, { tree: string }>) {
        const catalogId = getCatalogId(action.meta.tree);
        const catalog = state.catalogs[catalogId];
        if (!catalog) return;

        const currentIndex = catalog.searchFocusIndex ?? 0;
        const matchCount = catalog.searchMatches.length;
        if (matchCount === 0) return;

        const newIndex = currentIndex <= 0 ? matchCount - 1 : currentIndex - 1;
        catalog.searchFocusIndex = newIndex;
        catalog.searchFocusId = catalog.searchMatches[newIndex]?.node.id ?? null;
      },
    },
    toggleSearchPanel: {
      prepare(tree: string, open?: boolean) {
        return { meta: { tree }, payload: { open }, error: null };
      },
      reducer(
        state,
        action: PayloadAction<{ open?: boolean }, string, { tree: string }>,
      ) {
        const catalogId = getCatalogId(action.meta.tree);
        if (!state.catalogs[catalogId]) {
          state.catalogs[catalogId] = { ...initialCatalogSearchState };
        }
        const { open = !state.catalogs[catalogId].searchPanel } = action.payload;
        state.catalogs[catalogId].searchPanel = open;

        if (!open) {
          // Clear search when closing panel
          state.catalogs[catalogId].searchCondition = null;
          state.catalogs[catalogId].searchQuery = null;
          state.catalogs[catalogId].searchMatches = [];
          state.catalogs[catalogId].searchSuggestions = [];
          state.catalogs[catalogId].searchFocusIndex = null;
          state.catalogs[catalogId].searchFocusId = null;
        }
      },
    },
    clearSearch: {
      prepare(tree: string) {
        return { meta: { tree }, payload: null, error: null };
      },
      reducer(state, action: PayloadAction<null, string, { tree: string }>) {
        const catalogId = getCatalogId(action.meta.tree);
        if (state.catalogs[catalogId]) {
          state.catalogs[catalogId].searchCondition = null;
          state.catalogs[catalogId].searchQuery = null;
          state.catalogs[catalogId].searchMatches = [];
          state.catalogs[catalogId].searchSuggestions = [];
          state.catalogs[catalogId].searchFocusIndex = null;
          state.catalogs[catalogId].searchFocusId = null;
        }
      },
    },
    setHighlightFocus(
      state,
      action: PayloadAction<{ catalogId: string; highlightFocusId: string }>,
    ) {
      const { catalogId, highlightFocusId } = action.payload;
      if (state.catalogs[catalogId]) {
        state.catalogs[catalogId].highlightFocusId = highlightFocusId;
      }
    },
    clearCatalogSearch(state, action: PayloadAction<string>) {
      const catalogId = action.payload;
      delete state.catalogs[catalogId];
    },
    searchBySuggestionId: {
      prepare(tree: string, suggestionId: string, suggestionTitle: string) {
        return {
          meta: { tree },
          error: null,
          payload: { suggestionId, suggestionTitle },
        };
      },
      reducer(
        state,
        action: PayloadAction<
          { suggestionId: string; suggestionTitle: string },
          string,
          { tree: string }
        >,
      ) {
        const {
          meta: { tree },
          payload: { suggestionTitle },
        } = action;
        const catalogId = getCatalogId(tree);

        if (!state.catalogs[catalogId]) {
          state.catalogs[catalogId] = { ...initialCatalogSearchState };
        }

        const searchCondition = { by: SEARCH_BY.TEXT, condition: suggestionTitle };
        const searchQuery = toSearchQuery(searchCondition);

        state.catalogs[catalogId].searchCondition = searchCondition;
        state.catalogs[catalogId].searchQuery = searchQuery;
        // Note: searchMatches, searchFocusIndex, searchFocusId are set by the tree listener
        // after it finds matching nodes and updates tree data
      },
    },
    search: {
      prepare(tree: string) {
        return { meta: { tree }, payload: null, error: null };
      },
      reducer(state, action: PayloadAction<null, string, { tree: string }>) {
        const catalogId = getCatalogId(action.meta.tree);
        const catalog = state.catalogs[catalogId];
        if (!catalog?.searchCondition) return;

        // Convert searchCondition to searchQuery
        const searchQuery = toSearchQuery(catalog.searchCondition);
        catalog.searchQuery = searchQuery;
        // Note: searchMatches, searchFocusIndex, searchFocusId are set by the tree listener
        // after it finds matching nodes and updates tree data
      },
    },
    searchUpdate: {
      prepare(
        tree: string,
        catalogIdsList?: string[],
        item?: Item,
        structureReasonText?: string,
      ) {
        return {
          meta: { tree },
          error: null,
          payload: { catalogIdsList, item, structureReasonText },
        };
      },
      reducer(
        state,
        action: PayloadAction<
          {
            catalogIdsList?: string[];
            item?: Item;
            structureReasonText?: string;
          },
          string,
          { tree: string }
        >,
      ) {
        const {
          meta: { tree },
          payload: { catalogIdsList, item, structureReasonText },
        } = action;
        const catalogId = getCatalogId(tree);

        if (!state.catalogs[catalogId]) {
          state.catalogs[catalogId] = { ...initialCatalogSearchState };
        }

        // Update searchQuery with additional info
        state.catalogs[catalogId].searchQuery = {
          ...(state.catalogs[catalogId].searchQuery || {
            by: '',
            condition: '',
            regexp: '',
          }),
          updated: Date.now(),
          catalogIds: catalogIdsList,
          item,
          structureReasonText,
        };
        // Note: searchMatches, searchFocusIndex, searchFocusId are set by the tree listener
      },
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setMarket, () => ({ ...initialState }));
  },
});

export const {
  setSearchCondition,
  setSearchSuggestions,
  setSearchResults,
  setSearchFocus,
  moveSearchNext,
  moveSearchPrevious,
  toggleSearchPanel,
  clearSearch,
  setHighlightFocus,
  clearCatalogSearch,
  searchBySuggestionId,
  search,
  searchUpdate,
} = treeSearchSlice.actions;

// Action creator aliases for backward compatibility
export const searchSetConditionByFlag = (tree: string, flag: string) =>
  treeSearchSlice.actions.setSearchCondition(tree, SEARCH_BY.FLAG, flag);

export const searchSetConditionByText = (tree: string, text: string) =>
  treeSearchSlice.actions.setSearchCondition(tree, SEARCH_BY.TEXT, text);

export default treeSearchSlice.reducer;

// Selectors
const selectTreeSearch = (state: RootState) => state.treeSearch;

export const selectSearchConditionForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchCondition ?? null,
);

export const selectSearchQueryForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchQuery ?? null,
);

export const selectSearchMatchesForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) =>
    state.catalogs[catalogId]?.searchMatches ?? ReadOnlyArray.Empty,
);

export const selectSearchMatchesCountForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchMatches?.length ?? 0,
);

export const selectSearchFocusIdForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchFocusId ?? null,
);

export const selectSearchFocusIndexForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchFocusIndex ?? null,
);

export const selectSearchPanelOpenForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchPanel ?? false,
);

export const selectSearchSuggestionsForTree = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) =>
    state.catalogs[catalogId]?.searchSuggestions ?? ReadOnlyArray.Empty,
);

export const selectSearchCatalogHits = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) =>
    state.catalogs[catalogId]?.searchQuery?.catalogIds ?? ReadOnlyArray.Empty,
);

export const selectSearchItemHit = createReferenceEqualSelector(
  selectTreeSearch,
  getCatalogIdFromTreeId,
  (state, catalogId) => state.catalogs[catalogId]?.searchQuery?.item ?? null,
);

export const selectHighlightFocusIdForCatalog = createReferenceEqualSelector(
  selectTreeSearch,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => state.catalogs[catalogId]?.highlightFocusId ?? null,
);

export const selectSearchFocusForCatalog = createReferenceEqualSelector(
  selectTreeSearch,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => {
    const catalog = state.catalogs[catalogId];
    if (!catalog) return null;
    const { searchMatches, searchFocusIndex } = catalog;
    if (!searchMatches?.length) return null;
    // Return the node directly to maintain referential equality
    // Consumers can wrap if needed: { focusedNode: result }
    return searchMatches[searchFocusIndex ?? 0] ?? null;
  },
);

export const selectSearchFocusIdForCatalog = createReferenceEqualSelector(
  selectTreeSearch,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => state.catalogs[catalogId]?.searchFocusId ?? null,
);
